import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './Works.css';

const Works = () => {
  const works = [
    {
      id: 1,
      image: '/images/fracto-video-logo.png',
      title: 'FractoStudio — Online Video Editor',
      description: 'Fractallogy\'s free browser video editor for podcasters and creators. Sync multi-camera footage by audio, add captions and transitions, export Shorts and Reels presets, and download watermark-free videos — no install or sign-up required.',
      category: 'Creator Tools',
      color: '#a855f7',
      link: '/online-video-editor',
      external: false,
      browserFrame: true,
      browserAddress: 'onlinefreevideoeditor.com/edit',
      videoSrc: 'https://websitestorage.blr1.cdn.digitaloceanspaces.com/editor-fracto.mp4',
      fullVideo: true,
      brandImage: '/images/fracto-video-logo.png',
      brandImageWidth: '120px'
    },
    {
      id: 2,
      image: '/images/fracto-chat.gif',
      title: 'AI Customer Support',
      description: 'Say goodbye to long wait times! Our AI-powered support assistant helps your customers get instant answers, track orders, and resolve issues in seconds.',
      category: 'AI/ML',
      color: '#00f5ff',
      link: 'https://chatbot.fractallogy.com/',
      external: true,
      brandImage: '/images/fracto_ani.gif',
      brandImageWidth: '150px'
    },
    {
      id: 3,
      image: '/images/resource/Agriculture-2.jpg',
      title: 'Agriculture Domain Experts',
      description: 'We are a team of AgriTech specialists with deep expertise in applying AI and remote sensing to modern farming. Our smart field scouting system helps you maintain detailed crop records and organize team workflows seamlessly. Leverage our precision farming toolkit including fertilizer blend optimizer, crop protection selector, and spray sequence planner to make data-driven agricultural decisions.',
      category: 'AgriTech',
      color: '#00ff88',
      link: '#contact',
      external: false,
      openContactPopup: true,
      browserFrame: true,
      browserAddress: 'app.fractallogy.com/agri',
      slideImage: '/images/main-slider/farm.png'
    },
    {
      id: 4,
      image: '/images/main-slider/website_new.jpg',
      title: 'E-Commerce & Marketplace Solutions',
      description: 'We specialize in building scalable, secure, and high-performance e-commerce platforms tailored to your business needs. Our solutions include multi-vendor marketplaces, payment gateway integrations, inventory sync, order management, and mobile-first responsive designs that drive conversions and customer engagement.',
      category: 'E-Commerce',
      color: '#ff00ff',
      link: '#contact',
      external: false
    },
    {
      id: 5,
      image: '/images/main-slider/pos_crm.png',
      title: 'CRM, POS Solutions',
      description: 'Fracto CRM is an all-in-one billing and inventory system for optical and eyewear retailers. It seamlessly manages purchases, stock, sales, vendors, and detailed reports across multiple stores. Features include barcode scanning, GST-compliant invoicing, customer loyalty programs, prescription tracking and lens inventory management.',
      category: 'CRM, POS',
      color: '#ffaa00',
      link: 'https://opticalcrm.fractallogy.com/',
      external: true,
      browserFrame: true,
      browserAddress: 'app.fractallogy.com/crm',
      videoSrc: 'https://websitestorage.blr1.cdn.digitaloceanspaces.com/fractallogy_website/crm.mp4',
      fullVideo: true
    }
  ];

  const [visibleCards, setVisibleCards] = useState([]);
  const cardRefs = useRef([]);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observers = cardRefs.current.map((ref, index) => {
      if (!ref) return null;
      
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setVisibleCards((prev) => [...new Set([...prev, index])]);
            } else {
              setVisibleCards((prev) => prev.filter((i) => i !== index));
            }
          });
        },
        { threshold: 0.1, rootMargin: '100px 0px' }
      );
      
      observer.observe(ref);
      return observer;
    });

    return () => {
      observers.forEach((observer) => observer?.disconnect());
    };
  }, []);

  return (
    <section className="works" id="works" ref={sectionRef}>
      <div className="works-container">
        <div className="works-heading">
          <h2>Our Works</h2>
          <p>Innovative solutions that shape the future of technology</p>
        </div>
        
        <div className="works-showcase">
          {works.map((work, index) => (
            <div
              key={work.id}
              ref={(el) => (cardRefs.current[index] = el)}
              className={`work-item ${visibleCards.includes(index) ? 'visible' : ''} ${index % 2 === 0 ? 'left' : 'right'}`}
              style={{ '--delay': `${index * 0.1}s` }}
            >
              <div className={`work-item__image-container ${work.browserFrame ? 'browser-frame' : ''} ${work.fullVideo ? 'full-video' : ''}`}>
                {work.browserFrame && (
                  <div className="browser-header">
                    <div className="browser-dots">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                    <div className="browser-address">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                      </svg>
                      {work.browserAddress}
                    </div>
                  </div>
                )}
                {work.videoSrc ? (
                  <video
                    src={work.videoSrc}
                    poster={work.image}
                    className="work-item__image"
                    autoPlay
                    loop
                    muted
                    playsInline
                  />
                ) : (
                  <img src={work.image} alt={work.title} className="work-item__image" />
                )}
                {work.slideImage && (
                  <img
                    src={work.slideImage}
                    alt=""
                    className={`work-item__slide-image ${visibleCards.includes(index) ? 'slide-in' : ''}`}
                  />
                )}
                <div className="work-item__overlay"></div>
              </div>
              <div className="work-item__info">
                <div className="work-item__meta">
                  {work.brandImage && (
                    <img src={work.brandImage} style={{ width: work.brandImageWidth || '120px' }} alt="" />
                  )}
                  <span className="work-item__category">{work.category}</span>
                </div>
                <h3 className="work-item__title">{work.title}</h3>
                <p className="work-item__description">{work.description}</p>
                {work.external || work.openContactPopup ? (
                  <a
                    href={work.link}
                    className="work-item__cta"
                    {...(work.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                    {...(work.openContactPopup ? {
                      onClick: (e) => {
                        e.preventDefault();
                        window.dispatchEvent(new CustomEvent('openContactForm'));
                      }
                    } : {})}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M5 12h14M12 5l7 7-7 7"/>
                    </svg>
                    <span>View Project</span>
                  </a>
                ) : (
                  <Link to={work.link} className="work-item__cta">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M5 12h14M12 5l7 7-7 7"/>
                    </svg>
                    <span>View Project</span>
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
};

export default Works;
